/**
 * Блок «Сцена на торте (ИИ)» в «Визуалах МП» набора пряников (PP1).
 *
 * Поток платного шага: смета → подтверждение суммы → запуск. ИИ рисует только фон (торт сверху, без пряников и
 * текста), пряники вклеивает ПИМ, поэтому слайд «На торте» не искажает рисунок на прянике. Готовая сцена хранится у
 * набора; слайды и черновики набора после неё пересоздаются на бэкенде сами. Кнопка запуска скрыта без права
 * Gingerbread:APPROVE (оно же право платных запусков ИИ в генераторе пряников).
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import { AlertTriangle, ImageOff, Loader2, Sparkles } from 'lucide-react'
import { Badge } from '../../../components/ui/Badge'
import { Button } from '../../../components/ui/Button'
import { ConfirmDialog } from '../../../components/ui/ConfirmDialog'
import { AuthorizedMediaImage } from '../../../shared/media/authorizedMedia'
import { useToastStore } from '../../../stores/toastStore'
import { apiErrorInfo } from '../../gingerbread-generator/shapeMetrics'
import { gingerbreadCatalogAPI } from '../api'
import {
  formatSceneUsd,
  SCENE_QUOTE_ERROR_CODES,
  SCENE_STATUS_LABEL,
  SCENE_STATUS_VARIANT,
  SCENE_WARNING_HINTS,
  sceneConfirmMessage,
  type KitScene,
  type SceneQuote,
} from '../scene'

/** Пока сцена рисуется, состояние перечитывается с этим интервалом. */
const POLL_MS = 5000

const sectionTitle = 'mb-2 flex flex-wrap items-center gap-2 text-2xs font-semibold uppercase tracking-wider text-gray-500 dark:text-zinc-400'

export function KitSceneBlock({ article, canRun, onSceneReady }: {
  article: string
  /** Gingerbread:APPROVE: без него кнопка запуска не показывается. */
  canRun: boolean
  /** Сцена стала готовой (идущая операция закончилась успешно): родитель перечитывает визуалы — их пересоздание идёт само. */
  onSceneReady?: () => void
}) {
  const showToast = useToastStore((state) => state.showToast)
  const [scene, setScene] = useState<KitScene | null>(null)
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [quoting, setQuoting] = useState(false)
  const [quote, setQuote] = useState<{ value: SceneQuote; key: string } | null>(null)
  const [blockers, setBlockers] = useState<string[]>([])
  const [starting, setStarting] = useState(false)
  const previous = useRef<string | null>(null)

  const load = useCallback(
    async (silent = false) => {
      if (!silent) setLoading(true)
      try {
        setScene(await gingerbreadCatalogAPI.getScene(article))
        setLoadError(null)
      } catch (cause) {
        if (!silent) setLoadError(apiErrorInfo(cause, 'Не удалось загрузить сцену').message)
      } finally {
        if (!silent) setLoading(false)
      }
    },
    [article],
  )

  useEffect(() => {
    setScene(null)
    setQuote(null)
    setBlockers([])
    previous.current = null
    void load()
  }, [load])

  const running = scene?.status === 'running'
  useEffect(() => {
    if (!running) return
    const timer = window.setInterval(() => void load(true), POLL_MS)
    return () => window.clearInterval(timer)
  }, [running, load])

  // Закончившаяся удачно операция: слайды набора на бэкенде пересоздаются сами — родитель обновляет свой статус.
  useEffect(() => {
    const status = scene?.status ?? null
    if (previous.current === 'running' && status === 'ready') onSceneReady?.()
    previous.current = status
  }, [scene?.status, onSceneReady])

  const calculate = async () => {
    setQuoting(true)
    setBlockers([])
    try {
      const value = await gingerbreadCatalogAPI.quoteScene(article)
      if (value.blockers.length > 0 || !value.quote) {
        setBlockers(value.blockers.length > 0 ? value.blockers.map((entry) => entry.message || entry.code) : ['Расчёт не подписан: запуск сейчас недоступен'])
        return
      }
      setQuote({ value, key: gingerbreadCatalogAPI.newSceneKey() })
    } catch (cause) {
      showToast(apiErrorInfo(cause, 'Не удалось рассчитать смету').message, 'error')
    } finally {
      setQuoting(false)
    }
  }

  const start = async () => {
    if (!quote?.value.quote) return
    setStarting(true)
    try {
      await gingerbreadCatalogAPI.startScene(article, quote.value.quote, quote.key)
      showToast('Сцена на торте рисуется. Слайды и черновики карточек пересоздадутся сами, когда она будет готова.', 'info')
      setQuote(null)
      // Состояние «рисуется» появится сразу, как гейтвей поставит операцию.
      setScene((current) => ({ ...(current ?? { previewUrl: null, operationId: null, createdAt: null, warnings: [], error: null }), status: 'running' }))
      void load(true)
    } catch (cause) {
      const info = apiErrorInfo(cause, 'Не удалось запустить сцену')
      setQuote(null)
      if (SCENE_QUOTE_ERROR_CODES.has(info.code)) showToast('Смета устарела или изменились условия — рассчитайте её заново', 'warning')
      else showToast(info.message, 'error')
    } finally {
      setStarting(false)
    }
  }

  const status = scene?.status ?? 'none'
  const warningHints = (scene?.warnings ?? []).map((warning) => SCENE_WARNING_HINTS[warning.code]).filter((hint): hint is string => Boolean(hint))
  const actionLabel = status === 'ready' ? 'Пересоздать сцену (ИИ)' : 'Сцена на торте (ИИ)'

  return (
    <div data-testid="kit-scene-block" aria-label="Сцена на торте">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h5 className={sectionTitle}>
          Сцена на торте
          {scene && <Badge variant={SCENE_STATUS_VARIANT[status]}>{SCENE_STATUS_LABEL[status]}</Badge>}
        </h5>
        {canRun && (
          <Button
            size="sm"
            variant={status === 'ready' ? 'secondary' : 'primary'}
            icon={<Sparkles className="h-3.5 w-3.5" />}
            loading={quoting}
            disabled={loading || running || starting}
            onClick={() => void calculate()}
          >
            {actionLabel}
          </Button>
        )}
      </div>

      {loading ? (
        <div className="flex items-center py-3 text-gray-400">
          <Loader2 className="h-4 w-4 animate-spin" aria-label="Загрузка сцены" />
        </div>
      ) : loadError ? (
        <div role="alert" className="flex flex-wrap items-center gap-3 text-xs text-red-700 dark:text-red-300">
          <span>{loadError}</span>
          <Button size="sm" variant="secondary" onClick={() => void load()}>Повторить</Button>
        </div>
      ) : (
        <div className="flex flex-wrap items-start gap-3">
          {scene?.previewUrl && (
            <div className="w-28 shrink-0">
              <span className="flex aspect-[2/3] items-center justify-center overflow-hidden rounded-md border border-gray-200 bg-gray-50 dark:border-zinc-800 dark:bg-zinc-800">
                <AuthorizedMediaImage
                  src={scene.previewUrl}
                  alt="Сцена на торте: фон без пряников"
                  className="h-full w-full object-cover"
                  errorFallback={<ImageOff aria-label="Сцена недоступна" className="h-8 w-8 text-gray-300 dark:text-zinc-600" />}
                />
              </span>
            </div>
          )}
          <div className="min-w-0 flex-1 space-y-1.5 text-xs text-gray-600 dark:text-zinc-300">
            {running && (
              <p className="flex items-center gap-2" data-testid="kit-scene-running">
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                Сцена рисуется, страница обновится сама.
              </p>
            )}
            {status === 'none' && <p>Сцены пока нет: слайдов у набора три. ИИ нарисует фон с тортом, а пряники набора ПИМ вклеит сам.</p>}
            {status === 'ready' && <p>Сцена готова. Слайд «На торте» добавляется к слайдам набора при их создании.</p>}
            {scene?.error && (
              <p role="alert" className="flex items-start gap-1.5 text-red-700 dark:text-red-300">
                <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                <span>
                  {scene.error.message || 'Сцена не создана'}
                  {scene.error.code && <> <span className="font-mono text-2xs text-gray-400 dark:text-zinc-500">({scene.error.code})</span></>}
                </span>
              </p>
            )}
            {warningHints.map((hint) => (
              <p key={hint} className="flex items-start gap-1.5 text-yellow-700 dark:text-yellow-400">
                <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                <span>{hint}</span>
              </p>
            ))}
            {blockers.map((message) => (
              <p key={message} role="alert" className="flex items-start gap-1.5 text-red-700 dark:text-red-300">
                <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                <span>{message}</span>
              </p>
            ))}
          </div>
        </div>
      )}

      <ConfirmDialog
        isOpen={quote != null}
        title="Сцена на торте (ИИ)"
        message={quote ? sceneConfirmMessage(quote.value) : ''}
        confirmLabel={quote ? `Нарисовать за ≈ ${formatSceneUsd(quote.value.estimateUsd)}` : 'Нарисовать'}
        loading={starting}
        onConfirm={() => void start()}
        onClose={() => { if (!starting) setQuote(null) }}
      />
    </div>
  )
}
